import { useEffect, useState, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import "../../AppPage.css";
import { useNavigate, useParams } from "react-router-dom";
import { ContentTitleBar } from "../../../components/ContentTitleBar";
import useWebSocket from "react-use-websocket";
import { WEBSOCKET_URL } from "../../../store/config";
import {
  getNavigationUrl,
  checkStatusWhitelist,
} from "../../../store/navigationUtil";
import { CancelTitleBarMenu } from "../../../components/titleBarMenu/CancelTitleBarMenu";
import { GLIMPSE_QUEUED, GLIMPSE_RUNNING } from "../../../store/statuses";
import { loadClip } from "../utils/dataLoader";
import { useAuth } from "../../../store/auth/hooks";

export default function GlimpseRunningPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { accessCode } = useAuth();

  const [websocketRegistered, setWebsocketRegistered] = useState(false);
  const [chipStatus, setChipStatus] = useState("QUEUED");
  const { caseId, clipId } = useParams();

  const [clip, setClip] = useState(null);
  const [totalFiles, setTotalFiles] = useState(0);
  const [completedFiles, setCompletedFiles] = useState(0);

  const percentComplete =
    totalFiles > 0 ? (completedFiles / totalFiles) * 100.0 : 0;

  const load = useCallback(async () => {
    const currClip = await loadClip(caseId, clipId);
    if (!currClip) {
      navigate(getNavigationUrl(caseId));
    }
    if (!checkStatusWhitelist(currClip, [GLIMPSE_QUEUED, GLIMPSE_RUNNING])) {
      navigate(getNavigationUrl(caseId, currClip));
    }
    if (currClip.stats === GLIMPSE_QUEUED) {
      setChipStatus("QUEUED");
    } else {
      setChipStatus("RUNNING");
    }
    setClip(currClip);
  }, [caseId, clipId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Setup a websocket
   */
  const { sendMessage } = useWebSocket(WEBSOCKET_URL, {
    share: true,
    onOpen: () => {
      console.log("WebSocket connection established.", WEBSOCKET_URL);
    },
    onMessage: (event) => {
      if (event.data && event.data) {
        const data = JSON.parse(event.data);
        console.log("[WEBSOCKET]", data.data);
        if (
          data.command === "CLIP_STATUS" &&
          data.data?.clipId === clip?.id &&
          data.data.clipStatus !== "GLIMPSE_RUNNING" &&
          data.data.clipStatus !== "GLIMPSE_QUEUED"
        ) {
          // console.log(
          //   "[GlimpseRunningPage][WebSocket] New Status",
          //   data.data.clipStatus
          // );
          navigate(`/app/cases/${caseId}/clips/${clipId}`);
        } else if (
          data.command === "FILTER_STATUS_MESSAGE" &&
          data.data.clipId === Number(clipId)
        ) {
          // console.log(
          //   "[GlimpseRunningPage][WebSocket] PERCENT COMPLETE",
          //   data.data
          // );
          setTotalFiles(data.data.totalFiles);
          setCompletedFiles(data.data.completedFiles);
        }
      }
    },
  });

  useEffect(() => {
    if (!websocketRegistered) {
      setWebsocketRegistered(true);
      const payload = {
        command: "REGISTER",
        authorization: accessCode,
      };
      sendMessage(JSON.stringify(payload));
    }
  }, [accessCode, websocketRegistered, sendMessage]);

  return (
    <Stack component="main" height={1}>
      <Stack flex={1} gap={2} p={2}>
        <Paper elevation={4} sx={{ p: 2 }}>
          <Box display="flex" flexWrap="wrap" columnGap={6} rowGap={1}>
            <Box display="flex" flexDirection="column" justifyContent="center">
              <Typography variant="h5">GLIMPSE</Typography>
            </Box>
            <Box display="flex" flexDirection="column">
              <Typography variant="caption">GLIMPSE STATUS</Typography>
              <Chip
                sx={{
                  alignSelf: "start",
                  backgroundColor:
                    chipStatus === "QUEUED" ? "#b000f0" : "#00b976",
                  color: theme.palette.common.white,
                }}
                label={chipStatus}
              />
            </Box>
            {/* <Box display="flex" flexDirection="column">
              <Typography variant="caption">PROCESSED AUDIO FILES</Typography>
              <Typography variant="h5">{totalFiles}</Typography>
            </Box> */}
            <Box display="flex" flexDirection="column">
              <Typography variant="caption">PROGRESS</Typography>
              <Typography variant="h5">{percentComplete.toFixed()}%</Typography>
            </Box>
          </Box>
        </Paper>
        <Alert
          severity="info"
          variant="filled"
          sx={{ bgcolor: theme.palette.primary.dark, boxShadow: 4 }}
        >
          The system is now running GLIMPSE on the audio files. This may take a
          few minutes.
        </Alert>
      </Stack>
      <Paper
        elevation={4}
        sx={{
          p: 1,
          mx: 1,
          mb: 1,
        }}
      >
        <Button
          variant="outlined"
          onClick={async () => navigate(`/app/cases/${caseId}/clips`)}
        >
          RETURN TO CLIPS
        </Button>
      </Paper>
      <ContentTitleBar
        menu={<CancelTitleBarMenu caseId={caseId} clipId={clipId} />}
        name={clip?.description}
        label={clip?.description ? "GLIMPSE Running" : ""}
      />
    </Stack>
  );
}
